import React, { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';

export const PasswordStrengthIndicator = ({ password }) => {
  const [strength, setStrength] = useState({
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
    isValid: false,
  });

  useEffect(() => {
    const newStrength = {
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      hasMinLength: password.length >= 8 && password.length <= 20,
    };

    newStrength.isValid = Object.values(newStrength).every(Boolean);
    setStrength(newStrength);
  }, [password]);

  const indicators = [
    { label: 'Uppercase letter (A-Z)', met: strength.hasUpperCase },
    { label: 'Lowercase letter (a-z)', met: strength.hasLowerCase },
    { label: 'Number (0-9)', met: strength.hasNumber },
    { label: 'Special character (!@#$%...)', met: strength.hasSpecialChar },
    { label: '8-20 characters', met: strength.hasMinLength },
  ];

  if (!password) return null;

  return (
    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
      <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
      <div className="space-y-1">
        {indicators.map((indicator, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            {indicator.met ? (
              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
            ) : (
              <X className="w-4 h-4 text-gray-400 flex-shrink-0" />
            )}
            <span className={indicator.met ? 'text-green-700' : 'text-gray-600'}>
              {indicator.label}
            </span>
          </div>
        ))}
      </div>
      {password && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  strength.isValid
                    ? 'bg-green-500'
                    : indicators.filter(i => i.met).length >= 3
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
                style={{
                  width: `${(indicators.filter(i => i.met).length / indicators.length) * 100}%`,
                }}
              />
            </div>
            <span className="text-xs font-medium text-gray-600">
              {strength.isValid
                ? 'Strong'
                : indicators.filter(i => i.met).length >= 3
                ? 'Medium'
                : 'Weak'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const validatePassword = (password) => {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const hasMinLength = password.length >= 8 && password.length <= 20;

  return hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && hasMinLength;
};
